import { ReactNode, useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Users, Building2, FileText, HelpCircle, BarChart3, LogOut, Shield, Home, Briefcase, UserCheck, ShoppingCart, Ship, Menu, X, Database, Wrench, Droplets, ClipboardCheck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const adminNav = [
  { label: "Overview", href: "/admin", icon: Home },
  { label: "Approvals", href: "/admin/approvals", icon: UserCheck },
  { label: "Companies", href: "/admin/companies", icon: Building2 },
  { label: "Fleet Assignment", href: "/admin/fleet-assignment", icon: Ship },
  { label: "Clients", href: "/admin/users", icon: Users },
  { label: "Employees", href: "/admin/employees", icon: Briefcase },
  { label: "Auditors", href: "/admin/auditors", icon: ClipboardCheck },
  { label: "Documents", href: "/admin/documents", icon: FileText },
  { label: "Questions", href: "/admin/questions", icon: HelpCircle },
  { label: "Progress", href: "/admin/progress", icon: BarChart3 },
  { label: "Supply Chain", href: "/admin/supply-chain", icon: ShoppingCart },
  { label: "Tank Levels", href: "/admin/tank-levels", icon: Droplets },
  { label: "Crew Intel", href: "/admin/crew-intelligence", icon: Ship },
  { label: "PMS Builder", href: "/admin/pms-builder", icon: Wrench },
  { label: "Backend (ERP)", href: "/admin/backend", icon: Database },
];

const AdminLayout = ({ children }: { children: ReactNode }) => {
  const { user, loading, signOut, isAdmin, fullName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/employee-login");
    if (!loading && user && !isAdmin) navigate("/portal");
  }, [user, loading, isAdmin, navigate]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-deep flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || !isAdmin) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Admin sub-nav bar — desktop: horizontal scroll, mobile: hamburger */}
      <div className="bg-navy-deep border-b border-primary-foreground/5 mt-16 lg:mt-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between">

          {/* Mobile: hamburger + badge */}
          <div className="flex md:hidden items-center gap-3 py-3">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-teal" />
              <span className="text-[10px] font-bold tracking-[2px] uppercase text-teal font-body">Admin</span>
            </div>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="p-2 rounded-lg text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/5 transition-colors">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-navy-deep border-primary-foreground/10 w-[280px] p-0">
                <SheetHeader className="p-5 border-b border-primary-foreground/5">
                  <SheetTitle className="flex items-center gap-2 text-primary-foreground">
                    <Shield className="w-4 h-4 text-teal" />
                    <span className="text-sm font-bold tracking-wide font-body">Admin Console</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col py-2">
                  {adminNav.map((item) => {
                    const active = location.pathname === item.href;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.label}
                        to={item.href}
                        className={`flex items-center gap-3 px-5 py-3.5 text-sm font-body font-medium transition-all ${
                          active
                            ? "text-teal bg-teal/5 border-l-2 border-teal"
                            : "text-primary-foreground/50 hover:text-primary-foreground/80 hover:bg-primary-foreground/3 border-l-2 border-transparent"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    );
                  })}
                </nav>
                <div className="mt-auto border-t border-primary-foreground/5 p-5">
                  <p className="text-xs text-primary-foreground/40 font-body truncate mb-3">{fullName || user.email}</p>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      signOut().then(() => navigate("/employee-login"));
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-primary-foreground/40 hover:text-destructive hover:bg-destructive/5 transition-all font-body"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop: horizontal nav */}
          <div className="hidden md:flex items-center gap-1 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 pr-4 mr-2 border-r border-primary-foreground/10">
              <Shield className="w-3.5 h-3.5 text-teal" />
              <span className="text-[10px] font-bold tracking-[2px] uppercase text-teal font-body whitespace-nowrap">Admin</span>
            </div>
            {adminNav.map((item) => {
              const active = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`flex items-center gap-2 px-4 py-4 text-[13px] font-body font-medium tracking-wide whitespace-nowrap transition-all duration-200 border-b-2 ${
                    active
                      ? "border-teal text-teal"
                      : "border-transparent text-primary-foreground/40 hover:text-primary-foreground/70"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop: user info + sign out */}
          <div className="hidden md:flex items-center gap-3 py-3">
            <p className="text-xs text-primary-foreground/40 font-body truncate max-w-[140px]">{fullName || user.email}</p>
            <button
              onClick={() => signOut().then(() => navigate("/employee-login"))}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-primary-foreground/30 hover:text-destructive hover:bg-destructive/5 transition-all font-body"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>

          {/* Mobile: current page label */}
          <div className="flex md:hidden items-center py-3">
            <span className="text-xs text-primary-foreground/40 font-body">
              {adminNav.find((n) => n.href === location.pathname)?.label || "Admin"}
            </span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 bg-navy-deep">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
          {children}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminLayout;
